import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal } from "../landing/Reveal";
import {
  newSubmissionId,
  readUtmParams,
  savePartialLead,
  submitUseCaseForm,
} from "../../use-cases/submit";
import {
  FREE_MAIL_HINT,
  isFreeMailEmail,
} from "../../use-cases/shared/free-mail";
import { BookCallLink } from "../landing/BookCallLink";
import type {
  FormField,
  SelectFormField,
  UseCaseConfig,
} from "../../use-cases/types";

interface UseCaseFormProps {
  config: UseCaseConfig;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function fieldHasOther(
  field: FormField,
  value: string,
): field is SelectFormField {
  return field.type === "select" && !!field.allowOther && value === "__other__";
}

/**
 * Returns a validation error string for a single field, or null if valid.
 * Used per-step in the wizard to gate advancement.
 */
function validateField(
  field: FormField,
  value: string,
  otherValue: string,
): string | null {
  const trimmed = value.trim();
  if (field.required && !trimmed) {
    return `Please fill in “${field.label}”.`;
  }
  if (field.type === "email" && trimmed && !EMAIL_REGEX.test(trimmed)) {
    return "Please enter a valid email address.";
  }
  if (field.type === "textarea" && field.required && trimmed.length < 3) {
    return `Please add a little more detail to “${field.label}”.`;
  }
  if (fieldHasOther(field, trimmed) && !otherValue.trim()) {
    return `Please specify your answer for “${field.label}”.`;
  }
  return null;
}

/**
 * Use-case landing-page contact form. Multi-stage wizard: core fields are
 * grouped into pages by their `step` key (email first, then name, then
 * audience + program), then all vertical-specific fields land together in a
 * final step. Empty vertical-field set collapses the wizard to just the
 * core steps. Once the email page is done the lead is saved as `partial`.
 *
 * Anti-spam: a hidden honeypot input (`company_website`) is rendered
 * offscreen. Bots that auto-fill every field will populate it; submissions
 * where it's non-empty are silently dropped client-side.
 */
export const UseCaseForm = ({ config }: UseCaseFormProps) => {
  const { cta, slug } = config;

  const coreFields = useMemo(
    () => cta.fields.filter((f) => f.core),
    [cta.fields],
  );
  const verticalFields = useMemo(
    () => cta.fields.filter((f) => !f.core),
    [cta.fields],
  );

  /**
   * Group core fields into wizard pages by their `step` key — consecutive
   * fields sharing the same `step` value collapse to one page (so
   * first_name + last_name show together). Fields without a `step` value
   * each get their own page. Vertical fields always combine into the
   * final page.
   */
  const stepPages = useMemo<FormField[][]>(() => {
    const pages: FormField[][] = [];
    let lastStepKey: string | undefined = undefined;
    for (const field of coreFields) {
      const stepKey = field.step;
      if (stepKey && stepKey === lastStepKey) {
        pages[pages.length - 1].push(field);
      } else {
        pages.push([field]);
        lastStepKey = stepKey;
      }
    }
    if (verticalFields.length > 0) pages.push(verticalFields);
    return pages;
  }, [coreFields, verticalFields]);

  const totalSteps = stepPages.length;

  const [step, setStep] = useState(1);
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(cta.fields.map((f) => [f.key, ""])),
  );
  const [otherText, setOtherText] = useState<Record<string, string>>({});
  const [honeypot, setHoneypot] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [utm, setUtm] = useState<Record<string, string>>({});
  const formRef = useRef<HTMLFormElement>(null);
  const hasAdvanced = useRef(false);
  // One id per pass through the form: ties the partial save to the final
  // submit so the sheet upserts a single row.
  const submissionId = useRef(newSubmissionId());
  const partialSaved = useRef(false);

  useEffect(() => {
    if (step === 1 && !hasAdvanced.current) return;
    hasAdvanced.current = true;
    // Auto-focus first input after step transition renders
    const timer = setTimeout(() => {
      const el = formRef.current?.querySelector<HTMLElement>(
        'input:not([type="hidden"]):not([tabindex="-1"]), select, textarea',
      );
      el?.focus();
    }, 300); // after framer-motion transition
    return () => clearTimeout(timer);
  }, [step]);

  useEffect(() => {
    setUtm(readUtmParams(window.location.search));
  }, []);

  // Reset when switching configs (defensive — route remount usually handles it).
  useEffect(() => {
    setStep(1);
    setValues(Object.fromEntries(cta.fields.map((f) => [f.key, ""])));
    setOtherText({});
    setHoneypot("");
    setError("");
    setIsSubmitting(false);
    setIsSubmitted(false);
    submissionId.current = newSubmissionId();
    partialSaved.current = false;
  }, [slug, cta.fields]);

  const updateValue = (key: string, value: string) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  };

  // Fields shown on the current step (1-indexed into stepPages).
  const fieldsForStep = (s: number): FormField[] => stepPages[s - 1] ?? [];

  const validateStep = (s: number): string | null => {
    for (const field of fieldsForStep(s)) {
      const err = validateField(
        field,
        values[field.key] ?? "",
        otherText[field.key] ?? "",
      );
      if (err) return err;
    }
    return null;
  };

  const handleAdvance = async () => {
    const stepError = validateStep(step);
    if (stepError) {
      setError(stepError);
      return;
    }
    setError("");

    const email = values.work_email?.trim();
    if (
      !partialSaved.current &&
      !honeypot.trim() &&
      email &&
      fieldsForStep(step).some((f) => f.key === "work_email")
    ) {
      partialSaved.current = true;
      savePartialLead({
        config,
        workEmail: email,
        utm,
        submissionId: submissionId.current,
      });
    }

    if (step < totalSteps) {
      setStep(step + 1);
      return;
    }

    await handleSubmit();
  };

  const handleSubmit = async () => {
    if (honeypot.trim()) {
      setIsSubmitted(true);
      return;
    }

    setIsSubmitting(true);

    const finalValues: Record<string, unknown> = {};
    for (const field of cta.fields) {
      const raw = values[field.key]?.trim() ?? "";
      if (!raw) continue;
      if (fieldHasOther(field, raw)) {
        finalValues[field.key] = otherText[field.key]?.trim() ?? "";
      } else {
        finalValues[field.key] = raw;
      }
    }

    try {
      await submitUseCaseForm({
        config,
        values: finalValues,
        utm,
        submissionId: submissionId.current,
      });
      setIsSubmitted(true);
    } catch (err) {
      console.error("Submit error:", err);
      setError(
        "We couldn’t submit your request. Please check your connection and try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFinalStep = step === totalSteps;
  const buttonLabel = isSubmitting
    ? cta.sendingLabel
    : isFinalStep
      ? cta.submitLabel
      : cta.continueLabel;

  const stepFields = fieldsForStep(step);
  // When a step groups short text fields via a shared `step` key (first_name
  // + last_name), render them side-by-side. Grouped selects (audience size +
  // program) and vertical-field groups stay stacked: their labels are long
  // and they tend to mix selects and text inputs.
  const isSideBySideStep =
    stepFields.length > 1 &&
    stepFields[0].step !== undefined &&
    stepFields.every((f) => f.type === "text");

  return (
    <section
      id="talk-to-us"
      className="scroll-mt-(--site-header-h,4rem) bg-fwd-ink text-white py-24 md:py-32 px-6 md:px-16 lg:px-24"
    >
      <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-[1fr_minmax(0,460px)] gap-16 lg:gap-20 items-start">
        <Reveal>
          <div>
            <span className="text-fwd-yellow text-xs font-bold tracking-[0.2em] uppercase block mb-5">
              {cta.sectionEyebrow}
            </span>
            <h2 className="font-display text-[clamp(2rem,4vw,3.5rem)] font-normal leading-tight text-white mb-6">
              {cta.headlineLead}
              <span className="italic text-fwd-yellow">
                {cta.headlineEmphasis}
              </span>
              {cta.headlineTail}
            </h2>
            <p className="font-serif text-white/70 text-lg md:text-xl leading-relaxed max-w-xl">
              {cta.body}
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="bg-fwd-soft text-fwd-ink rounded-lg p-8 md:p-10 shadow-[0_8px_40px_rgba(0,0,0,0.4)]">
            <AnimatePresence mode="wait">
              {!isSubmitted ? (
                <motion.form
                  ref={formRef}
                  key={`step-${step}`}
                  initial={{ opacity: 0, x: 16 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -16 }}
                  transition={{ duration: 0.25 }}
                  className="flex flex-col gap-4"
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleAdvance();
                  }}
                >
                  {isSideBySideStep ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {stepFields.map((field) => (
                        <FormFieldInput
                          key={field.key}
                          idPrefix={slug}
                          field={field}
                          value={values[field.key] ?? ""}
                          onChange={(v) => updateValue(field.key, v)}
                          otherValue={otherText[field.key] ?? ""}
                          onOtherChange={(v) =>
                            setOtherText((prev) => ({
                              ...prev,
                              [field.key]: v,
                            }))
                          }
                        />
                      ))}
                    </div>
                  ) : (
                    stepFields.map((field) => (
                      <FormFieldInput
                        key={field.key}
                        idPrefix={slug}
                        field={field}
                        value={values[field.key] ?? ""}
                        onChange={(v) => updateValue(field.key, v)}
                        otherValue={otherText[field.key] ?? ""}
                        onOtherChange={(v) =>
                          setOtherText((prev) => ({ ...prev, [field.key]: v }))
                        }
                      />
                    ))
                  )}

                  {/* Honeypot — visually hidden, ignored by humans, filled by bots. */}
                  <input
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                    name="company_website"
                    className="absolute -left-[10000px] w-px h-px opacity-0 pointer-events-none"
                  />

                  {error && <p className="text-fwd-red text-sm">{error}</p>}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`self-start mt-2 px-7 py-3 rounded text-sm font-semibold tracking-wider uppercase transition-opacity disabled:opacity-50 ${
                      isFinalStep
                        ? "bg-fwd-red text-white hover:opacity-90"
                        : "bg-fwd-ink text-white hover:opacity-85"
                    }`}
                  >
                    {buttonLabel}
                  </button>
                </motion.form>
              ) : (
                <motion.div
                  key="confirm"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-4"
                >
                  <div className="text-fwd-red text-3xl mb-4">{"✦"}</div>
                  <h3 className="font-display text-2xl text-fwd-ink mb-3">
                    {cta.success.headline}
                  </h3>
                  <p className="font-serif text-fwd-mid text-base leading-relaxed">
                    {cta.success.body}
                  </p>
                </motion.div>
              )}
            </AnimatePresence>

            {!isSubmitted && totalSteps > 1 && (
              <div className="flex gap-2 mt-6">
                {Array.from({ length: totalSteps }).map((_, i) => {
                  const dot = i + 1;
                  return (
                    <div
                      key={dot}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        dot === step ? "bg-fwd-red" : "bg-fwd-ink/10"
                      }`}
                    />
                  );
                })}
              </div>
            )}
          </div>
          {!isSubmitted && <BookCallLink tone="dark" />}
        </Reveal>
      </div>
    </section>
  );
};

interface FieldInputProps {
  idPrefix: string;
  field: FormField;
  value: string;
  onChange: (v: string) => void;
  otherValue: string;
  onOtherChange: (v: string) => void;
}

const FormFieldInput = ({
  idPrefix,
  field,
  value,
  onChange,
  otherValue,
  onOtherChange,
}: FieldInputProps) => {
  const id = `${idPrefix}-${field.key}`;
  const baseClass =
    "w-full px-4 py-3 bg-white border-[1.5px] border-fwd-ink/10 rounded text-fwd-ink placeholder:text-fwd-mid/50 focus:border-fwd-red focus:outline-none transition-colors";

  if (field.type === "select") {
    return (
      <div className="flex flex-col gap-2">
        <label htmlFor={id} className="text-sm font-semibold tracking-wide">
          {field.label}
        </label>
        <div className="relative">
          <select
            id={id}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className={`${baseClass} appearance-none cursor-pointer pr-10`}
          >
            <option value="" disabled>
              Select an option
            </option>
            {field.options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
            {field.allowOther && <option value="__other__">Other</option>}
          </select>
          <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-fwd-mid text-xs pointer-events-none">
            ▾
          </span>
        </div>
        {field.allowOther && value === "__other__" && (
          <input
            type="text"
            placeholder="Please specify"
            value={otherValue}
            onChange={(e) => onOtherChange(e.target.value)}
            className={baseClass}
          />
        )}
      </div>
    );
  }

  if (field.type === "textarea") {
    return (
      <div className="flex flex-col gap-2">
        <label htmlFor={id} className="text-sm font-semibold tracking-wide">
          {field.label}
        </label>
        <textarea
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          className={`${baseClass} min-h-[120px] resize-y leading-relaxed`}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={id} className="text-sm font-semibold tracking-wide">
        {field.label}
      </label>
      <input
        id={id}
        type={field.type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder}
        aria-describedby={
          field.type === "email" && isFreeMailEmail(value)
            ? `${id}-hint`
            : undefined
        }
        className={baseClass}
      />
      {field.type === "email" && isFreeMailEmail(value) && (
        <p id={`${id}-hint`} className="text-fwd-mid text-sm">
          {FREE_MAIL_HINT}
        </p>
      )}
    </div>
  );
};
